import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, FileText } from "lucide-react";
import LeadForm from "../components/LeadForm";
import PageMeta from "../components/PageMeta";
import { waLink } from "../lib/api";
import { PRODUCTS } from "../data/content";

const ProductDetail = () => {
  const { slug } = useParams();
  const [showQuote, setShowQuote] = useState(false);
  const product = PRODUCTS.find((p) => p.slug === slug);

  if (!product) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-24" data-testid="product-not-found">
        <div className="text-center">
          <p className="font-heading text-2xl font-semibold text-ink">Product not found.</p>
          <Link to="/products" className="btn-outline mt-6">Back to catalogue</Link>
        </div>
      </main>
    );
  }

  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== slug).slice(0, 4);

  return (
    <main className="pt-[76px]" data-testid="product-detail-page">
      <PageMeta title={`${product.name} | AccuSurge Medical Solutions`} description={product.short_description} />
      <div className="container-x py-10 md:py-16">
        <nav className="flex items-center gap-2 text-xs text-slate-400" aria-label="Breadcrumb" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-brand-700">Home</Link> /
          <Link to="/products" className="hover:text-brand-700">Products</Link> /
          <Link to={`/products?category=${product.category}`} className="capitalize hover:text-brand-700">{product.category.replace(/-/g, " ")}</Link> /
          <span className="text-slate-600">{product.name}</span>
        </nav>

        <div className="mt-8 grid gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="product-image">
              <img src={product.image} alt={product.name} className="h-[320px] w-full object-cover md:h-[480px]" />
            </div>
          </div>

          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent-600">{product.category.replace(/-/g, " ")}</span>
            <h1 className="mt-3 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-ink" data-testid="product-name">{product.name}</h1>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{product.short_description}</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">{product.description}</p>

            {product.features?.length > 0 && (
              <div className="mt-8" data-testid="product-features">
                <h2 className="font-heading text-base font-semibold uppercase tracking-wider text-ink">Key Features</h2>
                <ul className="mt-4 grid gap-2.5">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-600" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.variants?.length > 0 && (
              <div className="mt-8" data-testid="product-variants">
                <h2 className="font-heading text-base font-semibold uppercase tracking-wider text-ink">Available Variants</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.variants.map((v) => <span key={v} className="chip">{v}</span>)}
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="product-cta-panel">
              <button onClick={() => setShowQuote(true)} className="btn-accent" data-testid="product-request-price">
                Request Price <ArrowRight className="h-4 w-4" />
              </button>
              <a href={waLink(`Hello AccuSurge, I would like to enquire about: ${product.name}`)} target="_blank" rel="noreferrer" className="btn-outline" data-testid="product-whatsapp">
                WhatsApp Enquiry
              </a>
              {product.brochure_url ? (
                <a href={product.brochure_url} className="inline-flex items-center gap-2 text-sm font-semibold text-brand-700" data-testid="product-brochure">
                  <FileText className="h-4 w-4" /> Brochure
                </a>
              ) : (
                <span className="text-xs text-slate-400">Brochure available on request</span>
              )}
            </div>
          </div>
        </div>

        {(product.specifications?.length > 0 || product.applications?.length > 0) && (
          <div className="mt-16 grid gap-12 lg:grid-cols-2">
            {product.specifications?.length > 0 && (
              <div data-testid="product-specifications">
                <h2 className="font-heading text-xl font-semibold text-ink">Technical Specifications</h2>
                <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <tbody>
                      {product.specifications.map((s, i) => (
                        <tr key={s.label} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                          <td className="px-5 py-3 font-medium text-slate-500">{s.label}</td>
                          <td className="px-5 py-3 text-slate-800">{s.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {product.applications?.length > 0 && (
              <div data-testid="product-applications">
                <h2 className="font-heading text-xl font-semibold text-ink">Applications</h2>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {product.applications.map((a) => (
                    <span key={a} className="rounded-lg border border-brand-100 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <h2 className="font-heading text-2xl font-semibold text-ink">Related Products</h2>
            <div className="mt-6 grid grid-cols-2 gap-5 lg:grid-cols-4">
              {related.map((p) => (
                <Link key={p.id} to={`/products/${p.slug}`} className="group overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`related-${p.slug}`}>
                  <div className="h-36 overflow-hidden">
                    <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="p-4 font-heading text-sm font-semibold leading-snug text-ink">{p.name}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {showQuote && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="product-quote-modal">
          <div className="absolute inset-0 bg-brand-950/70 backdrop-blur-sm" onClick={() => setShowQuote(false)} />
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <span className="eyebrow">Request a Quote</span>
                <h2 className="mt-2 font-heading text-2xl font-semibold text-ink">{product.name}</h2>
              </div>
              <button onClick={() => setShowQuote(false)} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:text-ink" aria-label="Close quote form" data-testid="product-quote-close">✕</button>
            </div>
            <LeadForm variant="quote" defaults={{ product_name: product.name }} compact />
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductDetail;
